import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FunctionWithFragments } from '@modernloop/shared/components';

import { JobStageInterviewSeatType, UseInterviewPlanDescription_JobStageFragment } from 'src/generated/mloop-graphql';

import { isBreak, isDayBreak } from 'src/utils/interview';

type Fragments = {
  jobStage: UseInterviewPlanDescription_JobStageFragment | undefined;
};

const useInterviewPlanDescription: FunctionWithFragments<Fragments, undefined, string> = ({ jobStage }) => {
  const { t, i18n } = useTranslation();

  const jobStageInterviews = useMemo(() => {
    return jobStage?.jobStageInterviewGroups?.map((group) => group.jobStageInterviews || []).flat() || [];
  }, [jobStage]);

  const jobStageInterviewSeats = useMemo(() => {
    return jobStageInterviews.map((interview) => interview.jobStageInterviewSeats || []).flat();
  }, [jobStageInterviews]);

  const interviewPlanMetaStr = useMemo(() => {
    if (
      !jobStageInterviews ||
      !jobStageInterviewSeats ||
      !jobStageInterviews.length ||
      !jobStageInterviewSeats.length
    ) {
      return '';
    }

    let durationInMins = 0;
    let interviews = 0;
    let minInterviewers = 0;
    let maxInterviewers = 0;

    jobStageInterviews.forEach((jobStageInterview) => {
      if (!jobStageInterview?.interviewType) {
        return;
      }

      if (!isBreak(jobStageInterview.interviewType) && !isDayBreak(jobStageInterview.interviewType)) {
        interviews++;
      }

      const duration = jobStageInterview.duration ?? 0;
      durationInMins += isDayBreak(jobStageInterview.interviewType) ? 1440 : (duration ?? 0);
    });

    jobStageInterviewSeats.forEach((jobStageInterviewSeat) => {
      const originalSeat =
        jobStageInterviewSeat.type === JobStageInterviewSeatType.Linked
          ? jobStageInterviewSeats.find(
              (seat) => seat.id === jobStageInterviewSeat?.linkedSeat?.linkedJobStageInterviewSeatId
            )
          : jobStageInterviewSeat;

      if (originalSeat?.type === JobStageInterviewSeatType.Freeform) {
        minInterviewers++;
        maxInterviewers++;
      } else if (originalSeat?.type === JobStageInterviewSeatType.Module) {
        minInterviewers++;
        maxInterviewers += 2;
      }
    });

    const locale = i18n.language || 'en';
    const listFormatter = new Intl.ListFormat(locale, { style: 'long', type: 'unit' });
    const numberFormatter = new Intl.NumberFormat(locale);

    const days = Math.floor(durationInMins / 1440);
    const hours = Math.floor((durationInMins / 60) % 24);
    const minutes = durationInMins % 60;

    const durationParts = [
      days > 0 ? new Intl.NumberFormat(locale, { style: 'unit', unit: 'day', unitDisplay: 'long' }).format(days) : null,
      hours > 0
        ? new Intl.NumberFormat(locale, { style: 'unit', unit: 'hour', unitDisplay: 'long' }).format(hours)
        : null,
      minutes > 0
        ? new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'long' }).format(minutes)
        : null,
    ].filter(Boolean) as string[];

    const durationStr =
      durationParts.length > 0
        ? listFormatter.format(durationParts)
        : new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'long' }).format(0);

    const interviewsStr = t(
      'hiring-dashboard-interviewer-portal:interviewer-portal.editInterviewLimitDialog.interviewsValue',
      {
        count: interviews,
      }
    );

    const interviewerNoun =
      minInterviewers === maxInterviewers && minInterviewers === 1
        ? t('hiring-dashboard-common:common.select.interviewer')
        : t('hiring-dashboard-common:common.select.interviewers');

    const interviewerCount =
      minInterviewers === maxInterviewers
        ? numberFormatter.format(minInterviewers)
        : `${numberFormatter.format(minInterviewers)} - ${numberFormatter.format(maxInterviewers)}`;

    const interviewersStr = `${interviewerCount} ${interviewerNoun}`;

    return listFormatter.format([interviewsStr, durationStr, interviewersStr]);
  }, [i18n.language, jobStageInterviews, jobStageInterviewSeats, t]);

  return interviewPlanMetaStr;
};

useInterviewPlanDescription.fragments = {
  jobStage: gql`
    fragment useInterviewPlanDescription_jobStage on JobStage {
      id
      jobStageInterviewGroups {
        id
        jobStageInterviews {
          id
          interviewType
          duration
          atsInterviewDefinition {
            atsId
            atsFields {
              ... on GreenhouseInterviewDefinition {
                estimatedMinutes
              }
            }
          }
          jobStageInterviewSeats {
            id
            type
            linkedSeat {
              linkedJobStageInterviewSeatId
            }
          }
        }
      }
    }
  `,
};

export default useInterviewPlanDescription;
